import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import type { Booking, Guest, Room, Payment, CanteenBill, CanteenItem, InventoryItem, StockTransaction, Vendor } from '@/mocks/data';

const HOTEL_NAME = 'Grand Hotel Pro';
const HOTEL_ADDRESS = '123, MG Road, Bengaluru, Karnataka 560001';
const HOTEL_GSTIN = '29AABCU9603R1ZX';
const HOTEL_PHONE = '+91 80 1234 5678';

interface JsPDFWithAutoTable extends jsPDF {
  autoTable: (...args: any[]) => void;
  lastAutoTable?: { finalY: number };
}

function setupDoc(): JsPDFWithAutoTable {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  doc.setFont('helvetica');
  return doc as JsPDFWithAutoTable;
}

function addHotelHeader(doc: JsPDFWithAutoTable, y: number): number {
  doc.setFontSize(18);
  doc.setTextColor(26, 43, 74);
  doc.setFont('helvetica', 'bold');
  doc.text(HOTEL_NAME, 105, y, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text(HOTEL_ADDRESS, 105, y + 5, { align: 'center' });
  doc.text(`GSTIN: ${HOTEL_GSTIN} | Phone: ${HOTEL_PHONE}`, 105, y + 9, { align: 'center' });
  doc.setDrawColor(201, 168, 76);
  doc.setLineWidth(0.5);
  doc.line(15, y + 12, 195, y + 12);
  return y + 16;
}

export function generateGSTInvoicePDF(
  booking: Booking,
  guest: Guest | undefined,
  room: Room | undefined,
  payments: Payment[],
  invoiceType: 'final' | 'proforma' = 'final',
) {
  const doc = setupDoc();
  let y = addHotelHeader(doc, 15);

  doc.setFontSize(14);
  doc.setTextColor(26, 43, 74);
  doc.setFont('helvetica', 'bold');
  doc.text(invoiceType === 'final' ? 'GST TAX INVOICE' : 'PROFORMA INVOICE', 15, y);
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text(`Invoice No: ${booking.booking_id}`, 15, y + 5);
  doc.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, 15, y + 9);

  doc.setFontSize(9);
  doc.text('Bill To:', 120, y);
  doc.setFont('helvetica', 'bold');
  doc.text(guest?.full_name || 'Guest', 120, y + 5);
  doc.setFont('helvetica', 'normal');
  if (guest?.phone) doc.text(`Phone: ${guest.phone}`, 120, y + 9);
  if (guest?.id_proof_number) doc.text(`${guest.id_proof_type}: ${guest.id_proof_number}`, 120, y + 13);

  y += 22;

  const nights = Math.ceil((new Date(booking.check_out).getTime() - new Date(booking.check_in).getTime()) / (1000 * 60 * 60 * 24));
  const roomTotal = nights * (room?.tariff || 0);
  const extrasTotal = booking.extra_charges.reduce((s, e) => s + e.amount, 0);
  const subtotal = roomTotal + extrasTotal;
  const cgst = subtotal * 0.06;
  const sgst = subtotal * 0.06;
  const grandTotal = subtotal + cgst + sgst;

  const body: (string | number)[][] = [];
  body.push(['Room Charges', `${room?.category || 'Room'} #${room?.room_number || ''}`, `${nights} nights x Rs.${room?.tariff || 0}`, roomTotal.toFixed(2)]);
  booking.extra_charges.forEach((e) => {
    body.push([e.name, 'Extra Service', '1', e.amount.toFixed(2)]);
  });
  body.push(['Subtotal', '', '', subtotal.toFixed(2)]);
  body.push(['CGST @ 6%', '', '', cgst.toFixed(2)]);
  body.push(['SGST @ 6%', '', '', sgst.toFixed(2)]);
  body.push(['Grand Total', '', '', grandTotal.toFixed(2)]);

  doc.autoTable({
    startY: y,
    head: [['Description', 'Details', 'Qty', 'Amount (Rs.)']],
    body,
    theme: 'grid',
    headStyles: { fillColor: [26, 43, 74], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, textColor: [50, 50, 50] },
    columnStyles: { 3: { halign: 'right' } },
    styles: { font: 'helvetica' },
  });

  const finalY = doc.lastAutoTable?.finalY || y + 40;

  doc.setFontSize(10);
  doc.setTextColor(26, 43, 74);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Summary', 15, finalY + 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);

  const paidTotal = payments.reduce((s, p) => s + p.amount, 0);
  doc.text(`Total Amount: Rs. ${grandTotal.toFixed(2)}`, 15, finalY + 16);
  doc.text(`Amount Paid: Rs. ${paidTotal.toFixed(2)}`, 15, finalY + 20);
  doc.text(`Amount Pending: Rs. ${(grandTotal - paidTotal).toFixed(2)}`, 15, finalY + 24);

  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('This is a computer generated invoice and does not require signature.', 105, 280, { align: 'center' });

  doc.save(`${invoiceType === 'final' ? 'GST-Invoice' : 'Proforma'}-${booking.booking_id}.pdf`);
}

export function generateReceiptPDF(payment: Payment, guestName: string, bookingId: string) {
  const doc = setupDoc();
  let y = addHotelHeader(doc, 15);

  doc.setFontSize(16);
  doc.setTextColor(26, 43, 74);
  doc.setFont('helvetica', 'bold');
  doc.text('PAYMENT RECEIPT', 105, y, { align: 'center' });
  y += 10;

  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text(`Receipt No: ${payment.receipt_no}`, 15, y);
  doc.text(`Date: ${new Date(payment.created_at).toLocaleDateString('en-IN')}`, 120, y);
  y += 8;
  doc.text(`Booking: ${bookingId}`, 15, y);
  y += 12;

  doc.autoTable({
    startY: y,
    body: [
      ['Guest Name', guestName],
      ['Amount Received', `Rs. ${payment.amount.toLocaleString('en-IN')}`],
      ['Payment Method', payment.payment_method.toUpperCase()],
      ['Payment Type', payment.payment_type.toUpperCase()],
    ],
    theme: 'grid',
    columnStyles: { 0: { fillColor: [245, 245, 245], fontStyle: 'bold' } },
    bodyStyles: { fontSize: 10, textColor: [50, 50, 50] },
    styles: { font: 'helvetica' },
  });

  const finalY = doc.lastAutoTable?.finalY || y + 30;

  doc.setFontSize(12);
  doc.setTextColor(201, 168, 76);
  doc.setFont('helvetica', 'bold');
  doc.text(`Rs. ${payment.amount.toLocaleString('en-IN')}`, 105, finalY + 15, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text('Thank you for your payment!', 105, finalY + 20, { align: 'center' });

  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('This is a computer generated receipt.', 105, 280, { align: 'center' });

  doc.save(`Receipt-${payment.receipt_no}.pdf`);
}

export function generateKOTPDF(tableOrRoom: string, cartItems: { name: string; qty: number; price: number }[]) {
  const doc = new jsPDF({ unit: 'mm', format: [80, 150] }) as JsPDFWithAutoTable;
  doc.setFont('helvetica');

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('KOT', 40, 10, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(HOTEL_NAME, 40, 15, { align: 'center' });
  doc.text(`${tableOrRoom} | ${new Date().toLocaleString('en-IN')}`, 40, 19, { align: 'center' });
  doc.line(5, 22, 75, 22);

  let y = 28;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Item', 6, y);
  doc.text('Qty', 50, y);
  doc.text('Amt', 68, y, { align: 'right' });
  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setDrawColor(200, 200, 200);
  doc.line(5, y, 75, y);
  y += 4;

  cartItems.forEach((item) => {
    doc.text(item.name, 6, y);
    doc.text(String(item.qty), 50, y);
    doc.text(`Rs.${(item.price * item.qty).toFixed(0)}`, 74, y, { align: 'right' });
    y += 5;
  });

  doc.line(5, y, 75, y);
  y += 5;
  const total = cartItems.reduce((s, c) => s + c.price * c.qty, 0);
  doc.setFont('helvetica', 'bold');
  doc.text(`Total: Rs. ${total.toFixed(0)}`, 74, y, { align: 'right' });

  doc.save(`KOT-${tableOrRoom.replace(/\s/g, '')}-${Date.now()}.pdf`);
}

export function generateCanteenBillPDF(bill: CanteenBill) {
  const doc = new jsPDF({ unit: 'mm', format: [80, 200] }) as JsPDFWithAutoTable;
  doc.setFont('helvetica');

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(HOTEL_NAME, 40, 8, { align: 'center' });
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(HOTEL_ADDRESS, 40, 12, { align: 'center' });
  doc.text(`GSTIN: ${HOTEL_GSTIN}`, 40, 15, { align: 'center' });
  doc.line(5, 18, 75, 18);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(bill.bill_no, 6, 24);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`${bill.table_or_room}`, 6, 28);
  doc.text(`${new Date(bill.created_at).toLocaleString('en-IN')}`, 74, 28, { align: 'right' });

  let y = 35;
  bill.items.forEach((item) => {
    doc.setFontSize(9);
    doc.text(`${item.qty}x ${item.name}`, 6, y);
    doc.text(`Rs. ${(item.price * item.qty).toFixed(0)}`, 74, y, { align: 'right' });
    y += 4;
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`  GST: Rs. ${item.gst.toFixed(2)}`, 6, y);
    doc.setTextColor(0, 0, 0);
    y += 4;
  });

  doc.line(5, y, 75, y);
  y += 4;
  doc.setFontSize(9);
  doc.text('Subtotal', 6, y);
  doc.text(`Rs. ${bill.subtotal.toFixed(2)}`, 74, y, { align: 'right' });
  y += 4;
  doc.text('GST Total', 6, y);
  doc.text(`Rs. ${bill.gst_total.toFixed(2)}`, 74, y, { align: 'right' });
  if (bill.discount > 0) {
    y += 4;
    const discountValue = bill.discount_type === 'percent' ? bill.subtotal * bill.discount / 100 : bill.discount;
    doc.text('Discount', 6, y);
    doc.text(`-Rs. ${discountValue.toFixed(2)}`, 74, y, { align: 'right' });
  }
  y += 4;
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL', 6, y);
  doc.text(`Rs. ${bill.total.toFixed(2)}`, 74, y, { align: 'right' });
  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`Payment: ${bill.payment_method.replace('_', ' ').toUpperCase()}`, 6, y);
  doc.text(`Status: ${bill.status.toUpperCase()}`, 74, y, { align: 'right' });

  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('Thank you for dining with us!', 40, 195, { align: 'center' });

  doc.save(`Bill-${bill.bill_no}.pdf`);
}

interface ReportColumn {
  header: string;
  dataKey: string;
}

export function generateReportPDF(title: string, columns: ReportColumn[], rows: Record<string, string | number>[], subtitle?: string) {
  const doc = setupDoc();
  let y = addHotelHeader(doc, 15);

  doc.setFontSize(14);
  doc.setTextColor(26, 43, 74);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 15, y);
  y += 6;
  if (subtitle) {
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, 15, y);
    y += 4;
  }
  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 15, y);
  y += 4;

  doc.autoTable({
    startY: y,
    head: [columns.map((c) => c.header)],
    body: rows.map((row) => columns.map((c) => String(row[c.dataKey] ?? ''))),
    theme: 'grid',
    headStyles: { fillColor: [26, 43, 74], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, textColor: [50, 50, 50] },
    styles: { font: 'helvetica' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
  });

  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('This is a computer generated report.', 105, 280, { align: 'center' });

  const safeTitle = title.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
  doc.save(`Report-${safeTitle}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function downloadCSV(filename: string, columns: { header: string; key: string }[], rows: Record<string, string | number>[]) {
  const header = columns.map((c) => `"${c.header}"`).join(',');
  const body = rows.map((row) => columns.map((c) => `"${String(row[c.key] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const csv = `${header}\n${body}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function generatePOPDF(
  poNumber: string,
  orderDate: string,
  expectedDelivery: string,
  vendorName: string,
  vendorAddress: string,
  vendorGstin: string,
  items: { name: string; qty: number; unit: string; unitPrice: number; amount: number }[],
  totalAmount: number,
  notes: string,
) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' }) as JsPDFWithAutoTable;
  doc.setFont('helvetica');

  let y = addHotelHeader(doc, 15);

  // Title
  doc.setFontSize(16);
  doc.setTextColor(26, 43, 74);
  doc.setFont('helvetica', 'bold');
  doc.text('PURCHASE ORDER', 105, y, { align: 'center' });
  y += 8;

  // PO Info
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text(`PO Number: ${poNumber}`, 15, y);
  doc.text(`Date: ${new Date(orderDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`, 105, y);
  y += 5;
  if (expectedDelivery) {
    doc.text(`Expected Delivery: ${new Date(expectedDelivery).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`, 15, y);
    y += 5;
  }
  y += 2;

  // Vendor info
  doc.setFontSize(10);
  doc.setTextColor(26, 43, 74);
  doc.setFont('helvetica', 'bold');
  doc.text('Vendor Details', 15, y);
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50);
  doc.setFont('helvetica', 'normal');
  doc.text(vendorName, 15, y);
  y += 4;
  if (vendorAddress) doc.text(vendorAddress, 15, y);
  y += 4;
  if (vendorGstin) doc.text(`GSTIN: ${vendorGstin}`, 15, y);
  y += 6;

  // Items table
  const body: (string | number)[][] = items.map((item) => [
    item.name,
    `${item.qty} ${item.unit}`,
    `Rs. ${item.unitPrice.toLocaleString('en-IN')}`,
    `Rs. ${item.amount.toLocaleString('en-IN')}`,
  ]);

  doc.autoTable({
    startY: y,
    head: [['Item', 'Quantity', 'Unit Price', 'Amount (Rs.)']],
    body,
    theme: 'grid',
    headStyles: { fillColor: [26, 43, 74], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, textColor: [50, 50, 50] },
    columnStyles: { 3: { halign: 'right' }, 2: { halign: 'right' } },
    styles: { font: 'helvetica' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });

  const finalY = doc.lastAutoTable?.finalY || y + 30;

  // Total
  doc.setFontSize(11);
  doc.setTextColor(26, 43, 74);
  doc.setFont('helvetica', 'bold');
  doc.text(`Total Amount: Rs. ${totalAmount.toLocaleString('en-IN')}`, 195, finalY + 8, { align: 'right' });

  // Notes
  if (notes) {
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text('Notes:', 15, finalY + 16);
    doc.text(notes, 15, finalY + 21);
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.setFont('helvetica', 'normal');
  doc.text('This is a computer generated purchase order.', 105, 280, { align: 'center' });
  doc.text('Authorized Signatory', 105, 272, { align: 'center' });

  doc.save(`PO-${poNumber}.pdf`);
}

export function generateGRNBillPDF(
  grnNumber: string,
  receivedDate: string,
  poNumber: string,
  vendorName: string,
  vendorAddress: string,
  vendorGstin: string,
  billNo: string,
  billAmount: number,
  items: { name: string; qty: number; unit: string; unitPrice: number; amount: number }[],
  notes: string,
) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' }) as JsPDFWithAutoTable;
  doc.setFont('helvetica');

  let y = addHotelHeader(doc, 15);

  // Title
  doc.setFontSize(16);
  doc.setTextColor(26, 43, 74);
  doc.setFont('helvetica', 'bold');
  doc.text('PURCHASE INVOICE / GOODS RECEIVED NOTE', 105, y, { align: 'center' });
  y += 8;

  // GRN & Bill Info
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text(`GRN Number: ${grnNumber}`, 15, y);
  doc.text(`Received Date: ${new Date(receivedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`, 105, y);
  y += 5;
  doc.text(`PO Reference: ${poNumber}`, 15, y);
  doc.text(`Bill Number: ${billNo || 'N/A'}`, 105, y);
  y += 7;

  // Vendor info
  doc.setFontSize(10);
  doc.setTextColor(26, 43, 74);
  doc.setFont('helvetica', 'bold');
  doc.text('Vendor Details', 15, y);
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50);
  doc.setFont('helvetica', 'normal');
  doc.text(vendorName, 15, y);
  y += 4;
  if (vendorAddress) { doc.text(vendorAddress, 15, y); y += 4; }
  if (vendorGstin) { doc.text(`GSTIN: ${vendorGstin}`, 15, y); y += 4; }
  y += 3;

  // Items table
  const body: (string | number)[][] = items.map((item) => [
    item.name,
    `${item.qty} ${item.unit}`,
    `Rs. ${item.unitPrice.toLocaleString('en-IN')}`,
    `Rs. ${item.amount.toLocaleString('en-IN')}`,
  ]);

  doc.autoTable({
    startY: y,
    head: [['Item', 'Qty Received', 'Unit Price', 'Amount (Rs.)']],
    body,
    theme: 'grid',
    headStyles: { fillColor: [26, 43, 74], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, textColor: [50, 50, 50] },
    columnStyles: { 0: { cellWidth: 60 }, 3: { halign: 'right' }, 2: { halign: 'right' } },
    styles: { font: 'helvetica' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });

  const finalY = doc.lastAutoTable?.finalY || y + 30;

  // Bill Amount
  doc.setFontSize(11);
  doc.setTextColor(26, 43, 74);
  doc.setFont('helvetica', 'bold');
  doc.text(`Bill Amount: Rs. ${billAmount.toLocaleString('en-IN')}`, 195, finalY + 8, { align: 'right' });

  // Notes
  if (notes) {
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text('Notes:', 15, finalY + 16);
    doc.text(notes, 15, finalY + 21);
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.setFont('helvetica', 'normal');
  doc.text('This is a computer generated purchase invoice.', 105, 280, { align: 'center' });
  doc.text('Authorized Signatory', 105, 272, { align: 'center' });

  doc.save(`GRN-Invoice-${grnNumber}.pdf`);
}